import { useNavigate } from 'react-router-dom';
import * as React from 'react';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import Avatar from '@mui/material/Avatar';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import { IconButton, InputAdornment } from '@mui/material';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Iconify from '../components/iconify/Iconify';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { getAuthToken } from '../services/Auth/Auth';
import { login } from "../store/actions/auth";
import Page from '../data/Page';
import { useAppContext } from '../contexts/AppContext';
import { useSelector } from 'react-redux';

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://mui.com/">
        Trooper Live
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

export default function SignIn() {
  const navigate = useNavigate()
  const { appState, setAppState } = useAppContext();
  const [showPassword, setShowPassword] = React.useState(false)
  const { isAuthenticated } = useSelector(state => state.auth)

  const validationSchema = yup.object({
    email: yup.string().required().min(3).label('Email'),
    password: yup.string().required().label('Password'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      await login(values.email, values.password).catch(() => {
        toast('Unable to login. Please try again.', { type: 'error' });
        formik.setFieldError('email', 'Email is incorrect');
        formik.setFieldError('password', 'Password is incorrect');
      })
    }
  })

  useEffect(() => {
    if (isAuthenticated) navigate("/")
  }, [isAuthenticated]);

  return (
    <>
      <Helmet>
        <title> Login | Troopers </title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0f172a 0%, #111827 50%, #0b1120 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 1.5 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,183,79,0.25) 0%, transparent 70%)',
            top: -100,
            right: -100,
            pointerEvents: 'none',
          }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 1.5 }}
          animate={{ opacity: 0.08, scale: 1 }}
          transition={{ duration: 1.8, ease: 'easeOut', delay: 0.2 }}
          style={{
            position: 'absolute',
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(79,70,229,0.25) 0%, transparent 70%)',
            bottom: -80,
            left: -80,
            pointerEvents: 'none',
          }}
        />
        <Container component="main" maxWidth="xs" sx={{ position: 'relative' }}>
          <CssBaseline />
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(16px)',
                borderRadius: 4,
                p: 4,
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.4, delay: 0.3, type: 'spring', stiffness: 200 }}
              >
                <Avatar sx={{ m: 1, bgcolor: 'primary.main', height: '70px', width: '70px' }} />
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.4 }}
              >
                <Typography gutterBottom sx={{ margin: '1.5rem 0', color: 'white' }} variant="h4">
                  Sign in to Troopers
                </Typography>
              </motion.div>
              <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                >
                  <TextField
                    label="Email Address"
                    name="email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    margin="normal"
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.06)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
                      '& .MuiInputBase-input': { color: 'white' },
                    }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.55 }}
                >
                  <TextField
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    margin="normal"
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.06)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
                      '& .MuiInputBase-input': { color: 'white' },
                    }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.65 }}
                >
                  <LoadingButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={!formik.isValid}
                    loading={formik.isSubmitting}
                    size='large'
                    sx={{ mt: 3 }}
                  >
                    Login
                  </LoadingButton>
                </motion.div>
              </form>
              <Copyright sx={{ mt: 4, color: 'rgba(255,255,255,0.5)' }} />
            </Box>
          </motion.div>
        </Container>
      </Box>
    </>
  );
}
